/**
 * The index of every party committee, as a table — and the arithmetic it
 * refuses to do.
 *
 * The FEC record holds 246 party committees that moved money to a candidate.
 * Four of them are the national campaign committees the LD-203 also names,
 * and the page gives those cards of their own; the rest are state and county
 * parties, plus the DNC and RNC. All of them belong in one sortable list, or
 * a reader has no way to find the party in their own state.
 *
 * Two rules shape the columns. Money out is never one figure: $411.4m of it
 * is 24A, advertising bought to attack a candidate, and a column headed
 * "money out" would invite a reader to compare a committee that gave to
 * campaigns with one that spent the same sum attacking them. So there is one
 * column per category, each carrying its own direction, and no column and no
 * row is ever totalled. Transactions are counted rather than added, which is
 * why the count is the one column that can honestly rank the whole list, and
 * so is where it opens.
 *
 * The ordering, the search and the cut can be done by the endpoint or here.
 * `resolve` decides which, by checking what came back rather than by
 * trusting that a parameter was understood: a server that ignores `page` and
 * answers with the whole index would otherwise leave the reader on page 1
 * forever with the pager insisting otherwise.
 */

import type { Effect, PartyCommittee, PartyCommitteeIndex } from "../api";
import { EFFECTS, EFFECT_ORDER, UNCLASSIFIED_FACTS, type EffectFacts } from "./committees";

/** Twenty-five rows. Each one carries an amount and the direction that
 *  amount was pointing, so the rows are two lines tall and forty of them
 *  would not fit on a screen. */
export const PER_PAGE = 25;

export type Way = "asc" | "desc";

export type Sort = { key: string; way: Way };

/** One column, and everything the header needs to say about itself.
 *
 *  `facts` is the load-bearing field, and it is the same `EffectFacts` the
 *  cards and the committee pages use. A money column without one would be an
 *  amount with no direction on it, which is the misreading this whole
 *  section exists to prevent. */
export type Column = {
  /** The token in the query string, and the token sent to the endpoint. */
  key: string;
  /** The header, short enough for a cell. */
  label: string;
  /** Which way the money was pointing. Null on the two columns whose value
   *  is not money: the committee's name and its transaction count. */
  facts: EffectFacts | null;
  /** Right-aligned, in tabular figures. */
  numeric: boolean;
  /** Which way the first click on this header sorts. A name reads forwards;
   *  money and counts are wanted largest first. */
  first: Way;
  /** What the column sorts on. */
  of: (committee: PartyCommittee) => number | string;
  /** The column named as a thing the table can be ordered by, for the line
   *  that says how it currently is. Not the header, which is clipped to fit
   *  a cell: "Attacking" over a column, "money spent attacking" in a
   *  sentence. */
  ordering: string;
  /** The longer form, for the header's tooltip: the full category label and
   *  the FEC codes behind it, so the reading can be checked. */
  about: string;
};

/** The category labels, shortened for a header. The full ones — "Given to
 *  the campaign", "Spent attacking them" — are three or four words and are
 *  kept in `about` and in the explainer above the table. */
const SHORT: Record<Effect, string> = {
  direct: "Given to campaign",
  coordinated: "Coordinated",
  supporting: "Supporting",
  opposing: "Attacking",
};

const ORDERING: Record<Effect, string> = {
  direct: "money given to campaigns",
  coordinated: "coordinated spending",
  supporting: "money spent supporting a candidate",
  opposing: "money spent attacking a candidate",
};

function outColumn(key: Effect): Column {
  const facts = EFFECTS[key];
  return {
    key,
    label: SHORT[key],
    facts,
    numeric: true,
    first: "desc",
    of: (committee) => committee.money_out[key] ?? 0,
    ordering: ORDERING[key],
    about: `${facts.label} — FEC ${facts.codes.join(", ")}`,
  };
}

export const NAME_COLUMN: Column = {
  key: "name",
  label: "Committee",
  facts: null,
  numeric: false,
  first: "asc",
  of: (committee) => committee.name,
  ordering: "name",
  about: "The name the FEC files the committee under",
};

/** What registered lobbyists disclosed giving, federal account only.
 *
 *  The federal figure rather than the whole, for the reason the cards give:
 *  a headquarters, recount or convention account has its own far higher
 *  limit and cannot be paid to a candidate, so only the federal part belongs
 *  in the same row as money out. The rest is shown under it rather than
 *  dropped. */
export const IN_COLUMN: Column = {
  key: "money_in",
  label: "In, from lobbyists",
  facts: null,
  numeric: true,
  first: "desc",
  of: (committee) => committee.money_in_federal,
  ordering: "money in from registered lobbyists",
  about: "Disclosed on LD-203 filings, to the federal account",
};

export const COUNT_COLUMN: Column = {
  key: "transactions",
  label: "Transactions",
  facts: null,
  numeric: true,
  first: "desc",
  of: (committee) => committee.transactions,
  ordering: "transaction count",
  about: "Payments to or against a named candidate, counted across all four categories",
};

/** The codes the corpus will not classify, kept as a column of their own.
 *
 *  Summed within the category, which is allowed, and never folded into one
 *  of the four, which is not: an honorarium paid to a candidate personally
 *  and recount spending argue for no election, and putting either in the
 *  nearest bucket would be a guess. Drawn only when there is money in it. */
export const UNCLASSIFIED_COLUMN: Column = {
  key: "unclassified",
  label: "Not classified",
  facts: UNCLASSIFIED_FACTS,
  numeric: true,
  first: "desc",
  of: unresolved,
  ordering: "unclassified spending",
  about: `${UNCLASSIFIED_FACTS.label} — FEC ${UNCLASSIFIED_FACTS.codes.join(", ")}`,
};

const MONEY = [IN_COLUMN, ...EFFECT_ORDER.map(outColumn)];

export const COLUMNS: Column[] = [NAME_COLUMN, ...MONEY, COUNT_COLUMN];

const EVERY_COLUMN = [...COLUMNS, UNCLASSIFIED_COLUMN];

/** Money filed under a transaction code the corpus refuses to classify. */
export function unresolved(committee: PartyCommittee): number {
  return Object.entries(committee.money_out)
    .filter(([key]) => !(EFFECT_ORDER as string[]).includes(key))
    .reduce((sum, [, amount]) => sum + amount, 0);
}

/** The columns for a given index.
 *
 *  The unclassified column appears only when a committee holds something in
 *  it, so it is never an empty column — and never a dropped one either. */
export function columnsFor(committees: PartyCommittee[]): Column[] {
  if (!committees.some((committee) => unresolved(committee) !== 0)) return COLUMNS;
  return [NAME_COLUMN, ...MONEY, UNCLASSIFIED_COLUMN, COUNT_COLUMN];
}

/** Transactions, largest first.
 *
 *  A count, not a sum of dollars, which is the only reason it can rank the
 *  whole list. Opening on any of the four money columns would rank 246
 *  committees on one category and read as a league table of what the parties
 *  spend; opening on the name would put a Virginia district committee at the
 *  top of the busiest record in the country. */
export const DEFAULT_SORT: Sort = { key: COUNT_COLUMN.key, way: "desc" };

export function columnFor(key: string): Column {
  return EVERY_COLUMN.find((column) => column.key === key) ?? COUNT_COLUMN;
}

/** A sort read off the query string, with anything unrecognised falling back
 *  to the default rather than to an empty table. */
export function sortFrom(key: string | null, way: string | null): Sort {
  const column = EVERY_COLUMN.find((held) => held.key === key);
  if (!column) return DEFAULT_SORT;
  return { key: column.key, way: way === "asc" || way === "desc" ? way : column.first };
}

/** How the table is currently ordered, in words.
 *
 *  Written out beside the arrow on the header rather than left to it. An
 *  arrow says which way; only a sentence says which way round "largest" is
 *  on a column of money and on a column of names, and a reader who has just
 *  sorted 246 committees by the money spent attacking a candidate should be
 *  told that is what they did. */
export function orderWords(sort: Sort): string {
  const column = columnFor(sort.key);
  if (column.numeric) {
    return `${column.ordering}, ${sort.way === "asc" ? "smallest first" : "largest first"}`;
  }
  return `${column.ordering}, ${sort.way === "asc" ? "A to Z" : "Z to A"}`;
}

/** What clicking a header does: sort by it, or turn the ordering round if it
 *  is already the sorted column. */
export function toggled(sort: Sort, column: Column): Sort {
  if (sort.key !== column.key) return { key: column.key, way: column.first };
  return { key: column.key, way: sort.way === "asc" ? "desc" : "asc" };
}

export function pageFrom(raw: string | null): number {
  const page = Number(raw ?? 1);
  return Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
}

/** The single token the endpoint takes: the column, with a leading minus for
 *  descending.
 *
 *  Our own key goes across as it stands. The four money-out columns are sent
 *  as categories rather than as one "money out", because a total across them
 *  is the figure this section refuses to compute and so is a figure it
 *  cannot order on. A server that does not know a token answers in its own
 *  order, which `resolve` then declines. */
export function sortParam(sort: Sort): string {
  return `${sort.way === "desc" ? "-" : ""}${sort.key}`;
}

/** Name search, matched the way the endpoint's `q` is described: on the
 *  committee's name, case-insensitively, anywhere in it. */
export function matches(committee: PartyCommittee, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return committee.name.toLowerCase().includes(needle);
}

export type View = {
  rows: PartyCommittee[];
  /** Every committee the search matched, not just the ones on this page. */
  total: number;
  page: number;
  pages: number;
  /** Whether the endpoint did the filtering, ordering and cutting, or this
   *  module did. Not shown to a reader; it is what the tests assert on. */
  served: boolean;
};

/** The comparator for one column, ignoring ties. */
function rankBy(column: Column, way: Way) {
  return (one: PartyCommittee, two: PartyCommittee): number => {
    const left = column.of(one);
    const right = column.of(two);
    const order =
      typeof left === "string" && typeof right === "string"
        ? left.localeCompare(right)
        : Number(left) - Number(right);
    return way === "asc" ? order : -order;
  };
}

/** Sorted, with ties broken by name so that a page boundary falls in the
 *  same place twice. Most of these committees have nothing at all in most
 *  categories, so ties are the common case rather than the edge one. */
export function ordered(committees: PartyCommittee[], sort: Sort): PartyCommittee[] {
  const rank = rankBy(columnFor(sort.key), sort.way);
  return [...committees].sort((one, two) => rank(one, two) || one.name.localeCompare(two.name));
}

/** Filtered, ordered and cut to a page, here in the browser.
 *
 *  The page is clamped rather than trusted: a reader who arrives on `?page=9`
 *  after a search that matches three committees gets the last page there is,
 *  not an empty table. */
export function clientView(
  committees: PartyCommittee[],
  sort: Sort,
  q: string,
  page: number,
  perPage = PER_PAGE,
): View {
  const found = ordered(
    committees.filter((committee) => matches(committee, q)),
    sort,
  );
  const pages = Math.max(1, Math.ceil(found.length / perPage));
  const on = Math.min(Math.max(1, page), pages);
  return {
    rows: found.slice((on - 1) * perPage, on * perPage),
    total: found.length,
    page: on,
    pages,
    served: false,
  };
}

function counted(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * The endpoint's answer, if it is one this table can use.
 *
 * Every field is read defensively and then checked against what was asked
 * for, because "the parameter was accepted" and "the parameter was applied"
 * are different facts and only one of them is visible in the payload. The
 * answer is used only when all of it holds:
 *
 *   - the paging fields are there and are numbers;
 *   - the page is no longer than one page, so the whole index coming back
 *     under the old shape is not mistaken for a page of it;
 *   - it is the page that was asked for, clamped to the last one;
 *   - every row matches the search;
 *   - the rows are in the order the sorted column would put them.
 *
 * The order is checked for monotonicity rather than against our own
 * comparator, so a server that breaks ties differently is still accepted.
 * Anything else and the caller sorts the index itself.
 */
export function serverView(
  answer: PartyCommitteeIndex | null,
  sort: Sort,
  q: string,
  page: number,
  perPage = PER_PAGE,
): View | null {
  if (!answer || !Array.isArray(answer.committees)) return null;

  const total = counted(answer.total);
  const served = counted(answer.page);
  const reported = counted(answer.pages);
  if (total === null || served === null || reported === null) return null;

  const rows = answer.committees;
  if (rows.length > perPage) return null;

  const pages = Math.max(1, reported || Math.ceil(total / perPage));
  if (served !== Math.min(Math.max(1, page), pages)) return null;
  if (!rows.every((committee) => matches(committee, q))) return null;

  const rank = rankBy(columnFor(sort.key), sort.way);
  const inOrder = rows.every((row, index) => index === 0 || rank(rows[index - 1], row) <= 0);
  if (!inOrder) return null;

  return { rows, total, page: served, pages, served: true };
}

/**
 * One page of the table, from wherever it can honestly be had.
 *
 * `answer` is the parameterised request, which may be a page, may be the
 * whole index under the old shape, and may be null while it is in flight or
 * after it failed. `held` is the index the page already fetched for the
 * cards above, which is the whole of it, so the table has something correct
 * to draw in every one of those cases.
 */
export function resolve(
  answer: PartyCommitteeIndex | null,
  held: PartyCommittee[],
  sort: Sort,
  q: string,
  page: number,
  perPage = PER_PAGE,
): View {
  const fromServer = serverView(answer, sort, q, page, perPage);
  if (fromServer) return fromServer;
  const all = held.length > 0 ? held : (answer?.committees ?? []);
  return clientView(all, sort, q, page, perPage);
}
